import i18n from "@modules/shared/localization/i18n";
import { AnalyticsEventName } from "@shopify/hydrogen-react";
import { useViewAnalytics } from "@modules/shopify/analytics/useViewAnalytics";
import {
  SingleCollectionPage,
  resolveSortOption,
} from "@modules/shop/components/Collection";
import {
  COLLECTION_PAGE_SIZE,
  collectionInfiniteQueryOptions,
  collectionQueryOptions,
} from "@modules/shopify/hooks";
import { getCountryVariables } from "@/modules/shared/localization/const/countries";
import { Link, createFileRoute, notFound } from "@tanstack/react-router";
import { seo } from "@utils/seo";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute(
  "/$locale/_layout/(public)/_public/collections/$handle",
)({
  component: CollectionRoute,
  validateSearch: (search: Record<string, unknown>) => {
    const sort = typeof search.sort === "string" ? search.sort : undefined;
    return sort && sort !== "default" ? { sort } : {};
  },
  ssr: true,
  loaderDeps: ({ search }) => ({
    sort: search.sort,
  }),
  loader: async ({ context: { queryClient }, params, deps }) => {
    if (!params.handle || params.handle === "") {
      notFound({ throw: true });
    }
    const localeVars = getCountryVariables(params.locale);
    const sortOpt = resolveSortOption(deps.sort);

    const baseVars = {
      handle: params.handle,
      numberOfProducts: COLLECTION_PAGE_SIZE,
      sortKey: sortOpt.sortKey,
      reverse: sortOpt.reverse,
      ...localeVars,
    };

    const [collectionData] = await Promise.all([
      queryClient.ensureQueryData(collectionQueryOptions(baseVars)),
      queryClient.ensureInfiniteQueryData(
        collectionInfiniteQueryOptions(baseVars),
      ),
    ]);

    return collectionData;
  },
  headers: () => ({
    "Cache-Control":
      "public, max-age=300, s-maxage=300, stale-while-revalidate=86400",
  }),
  staleTime: 60_000,
  head: ({ params, loaderData }) => {
    const t = i18n.getFixedT(params.locale);
    const collection = loaderData?.collection;
    const collectionName = collection?.title ?? params.handle;
    return {
      meta: [
        ...seo({
          title:
            collection?.seo.title ||
            t("Seo:collection.title", { name: collectionName }),
          description:
            collection?.seo.description ||
            t("Seo:collection.description", { name: collectionName }),
          image: collection?.image?.url,
        }),
      ],
    };
  },
});

function CollectionRoute() {
  const { t } = useTranslation();
  const { handle, locale } = Route.useParams();
  const loaderData = Route.useLoaderData();
  const collection = loaderData.collection;
  useViewAnalytics(
    collection
      ? {
          eventName: AnalyticsEventName.COLLECTION_VIEW,
          collectionHandle: collection.handle,
          collectionId: collection.id,
        }
      : null,
    collection?.id ?? null,
  );

  if (!collection) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <h1 className="text-xlarge text-text-1 mb-4 font-bold">
            {t("Collections.notFound")}
          </h1>
          <Link
            to="/$locale/collections"
            params={{ locale }}
            className="text-primary hover:text-primary-dark"
          >
            {t("Collections.backToCollections")}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <SingleCollectionPage
      handle={handle}
      locale={locale}
      collection={collection}
      productCount={collection.products.nodes.length}
    />
  );
}
